import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { IconDefinitionConstants } from "utils/constants";
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import {
  DashboardButtonGroup,
  PageLayouts,
  useMountedState,
  useNavigationHistory,
  IconTitle,
  TitleIcon,
  StyledButton,
  Icon,
  RouteBreadCrumb,
  ViewPanel,
  MasonryPanel,
  WidgetArea,
  ChartContextProvider,
  ChartToggle,
  GenericCommentsView,
} from "components/common";
import ArticleDetails from "./details";
import InvoiceList from "./invoiceList";
import {
  ArticleInvoicedQuantitiesBarChart,
  ArticleCustomersSummaryDoughnutChart,
  ArticleStatisticsSummeryNumberChart,
} from "./widgets";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useCreateInvoice, useInvoicePermissions } from "components/invoice";
import { Grid, Skeleton } from "@mui/material";
import { useArticleService } from "serviceHooks";
import useRemoveArticleAction from "./useRemoveArticleAction";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import { TextSkeleton } from "components/common/skeletons";
import clsx from "clsx";

const ArticleProfileDashboard: React.FC = (props) => {
  const { getMounted } = useMountedState();
  const urlParams = useParams<EntityIdParams>();
  const id = Number.parseInt(urlParams.id ?? "0");
  const location = useLocation();
  const navigate = useNavigate();
  const articleService = useArticleService();

  const { canCreateInvoices, canUseInvoices, hasChartsPermissions } = useInvoicePermissions();

  const onCreateInvoice = useCreateInvoice({ productId: id });

  const [article, setArticle] = React.useState<WintSuperkollAPIModel.Article>();
  const { removeArticleMoreAction } = useRemoveArticleAction(article?.id ?? 0, article?.canRemove ?? false);

  const { goBack } = useNavigationHistory();

  const loadArticle = (articleId: number) => {
    articleService
      .get(articleId)
      .then((result) => {
        if (getMounted()) {
          setArticle(result);
        }
      })
      .catch(() => {
        if (article)
          setTimeout(() => {
            goBack();
          }, 1000);
      });
  };

  React.useEffect(() => {
    const articleId = urlParams.id;
    if (articleId && article?.id !== Number.parseInt(articleId)) {
      loadArticle(Number.parseInt(articleId));
    }
  }, [urlParams]);

  const onSettingsClick = () => {
    navigate(UrlHelper.formatPath(`/Article/${id}/Settings`), {
      state: { background: location },
    });
  };

  return (
    <PageLayouts.DashboardLayout
      entityKey={id}
      hasPermissions={canUseInvoices}
      title={
        article ? (
          <IconTitle
            title={article.text ?? ""}
            subtitle={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Article)}
            icon={
              <TitleIcon
                icon={IconDefinitionConstants.WBox}
                displayType={article.inactive ? StyleEnums.DisplayType.None : undefined}
              />
            }
            extraTags={
              article.inactive
                ? [
                    {
                      tags: [LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.InActive)],
                    },
                  ]
                : undefined
            }
            defaultTagColor={article.inactive ? cssExports.grayTag : undefined}
            defaultTagSurface="background"
          ></IconTitle>
        ) : (
          <Grid container spacing={2} className={cssExports.iconTitleContainer}>
            <Grid item className={cssExports.dashboardHeaderIcon}>
              <Skeleton variant="circular" className={clsx(cssExports.supplierAvatar)} />
            </Grid>
            <Grid item xs container direction={"column"}>
              <Grid item className={cssExports.dashboardHeaderText}>
                <Skeleton variant="text" sx={{ fontSize: "8rem" }} width="40%" />
              </Grid>
              <Grid item container direction="row" spacing={1}>
                <Grid item>
                  <Skeleton variant="text" sx={{ fontSize: "2rem" }} width={80} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )
      }
      breadCrumb={<RouteBreadCrumb className={cssExports.dashBoardNavigation} current={article?.text ?? ""} />}
      topRightButtons={
        canCreateInvoices ? (
          <DashboardButtonGroup
            buttons={[
              <StyledButton size="sm" onClick={onCreateInvoice}>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CreateInvoice)}
              </StyledButton>,
              ACTION_SEPARATOR,
              <StyledButton size="sm" onClick={onSettingsClick} className={cssExports.iconButton}>
                <Icon icon={IconDefinitionConstants.WSettings} />
              </StyledButton>,
            ]}
            moreActions={[removeArticleMoreAction]}
          />
        ) : undefined
      }
    >
      {hasChartsPermissions() ? (
        <MasonryPanel>
          {article ? (
            <ArticleDetails article={article} />
          ) : (
            <Grid container className={clsx(cssExports.detailsTableView)}>
              <TextSkeleton lines={7} />
            </Grid>
          )}
          <WidgetArea>
            <ChartContextProvider>
              <ViewPanel
                title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Article.InvoicedQuantities)}
                appearance="sm"
                isCanvasContent
                topRightContent={<ChartToggle />}
              >
                <ArticleInvoicedQuantitiesBarChart articleId={id} />
              </ViewPanel>
            </ChartContextProvider>
          </WidgetArea>
          <WidgetArea>
            <ViewPanel
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Article.Last12Months)}
              appearance="sm"
              gridSize="md"
            >
              <ArticleCustomersSummaryDoughnutChart articleId={id} />
            </ViewPanel>

            <ViewPanel
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Article.Last12Months)}
              appearance="sm"
              gridSize="md"
            >
              <ArticleStatisticsSummeryNumberChart articleId={id} />
            </ViewPanel>
          </WidgetArea>
        </MasonryPanel>
      ) : article ? (
        <MasonryPanel children={[<ArticleDetails article={article} />]} />
      ) : (
        <></>
      )}

      {article && (
        <WidgetArea>
          <InvoiceList articleId={article?.id} />
        </WidgetArea>
      )}
      {article && (
        <Grid item xs={12}>
          <GenericCommentsView
            key={`comments-${article?.unitId}`}
            parentId={article?.id ?? 0}
            type={WintSuperkollAPIModel.GenericCommentType.InvoiceProduct}
            loading={article === undefined}
          />
        </Grid>
      )}
    </PageLayouts.DashboardLayout>
  );
};

export default ArticleProfileDashboard;
